import moment from 'moment';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Link, RowContainer } from '../../shared/styles';
import {
  publishFund,
  unpublishFund,
  activateFund,
  deactivateFund,
} from '../../../../services/lwadmin/fund/fund-service';
import FundsContext from './context';

import {
  ActionColumn,
  NameColumn,
  CompanyColumn,
  PartnershipTypeColumn,
  StatusColumn,
} from './styles';

const DecoNameColumn = styled(NameColumn)`
  & > .fa-warning {
    color: ${props => props.theme.errorColor};
    font-size: 16px;
    margin-left: 8px;
  }

  a.data-review {
    display: block;
    margin-top: 8px;
  }
`;

/* eslint-disable complexity */
export default function Fund({ fund }) {
  const { dispatch } = React.useContext(FundsContext);

  const {
    id,
    nameAndStock,
    published,
    active,
    publicUrl,
    company,
    publishable,
    dataReviews,
  } = fund;

  const { partnership } = company;
  const hasDataReview = dataReviews.length > 0;

  const publish = e => {
    e && e.preventDefault();

    publishFund(id).then(() => (dispatch({ type: 'publishFund', payload: id })));
  };

  const unpublish = e => {
    e && e.preventDefault();

    unpublishFund(id).then(() => (dispatch({ type: 'unpublishFund', payload: id })));
  };

  const activate = e => {
    e && e.preventDefault();

    activateFund(id).then(() => (dispatch({ type: 'activateFund', payload: id })));
  };

  const deactivate = e => {
    e && e.preventDefault();

    deactivateFund(id).then(() => (dispatch({ type: 'deactivateFund', payload: id })));
  };

  const getStatus = () => {
    if (published) {
      return 'Published';
    } if (active) {
      return 'Preview';
    }
    return 'Inactive';
  };

  const status = getStatus();

  const showDataReview = dataReview => e => {
    e && e.preventDefault();
    const payload = { dataReview, fund };
    dispatch({ type: 'showDataReviewModal', payload });
  };

  return (
    <RowContainer>
      <DecoNameColumn>
        {nameAndStock}
        {hasDataReview && (
          <i className="fa fa-warning" />
        )}
        {dataReviews.map(review => (
          <a
            href="#"
            key={review.createdAt}
            onClick={showDataReview(review)}
            className="data-review"
          >
            {moment(review.createdAt).format('DD/MM/YY @ h.mma')}
          </a>
        ))}
      </DecoNameColumn>
      <CompanyColumn>{company.name}</CompanyColumn>
      <PartnershipTypeColumn>{partnership ? partnership.type : 'None'}</PartnershipTypeColumn>
      <StatusColumn>{status}</StatusColumn>
      <ActionColumn>
        {!!active && <Link href={publicUrl} target="_blank">View</Link>}
        {!active && <Link href={`/lwadmin/fund/${id}`} target="_blank">View</Link>}
        |
        <Link href={`/lwadmin/fund/${id}/edit`}>Edit</Link>
        |
        {!!company.id && (
          <>
            <Link href={`/lwadmin/company/${company.id}`}>View Company</Link>
            |
          </>
        )}
        {published && (
          <>
            <Link href="#" onClick={unpublish}>Unpublish</Link>
            |
          </>
        )}
        {!published && publishable && active && (
          <>
            <Link href="#" onClick={publish}>Publish</Link>
            |
          </>
        )}
        {!!active && <Link href="#" onClick={deactivate}>Deactivate</Link>}
        {!active && <Link href="#" onClick={activate}>Activate</Link>}
      </ActionColumn>
    </RowContainer>
  );
}
/* eslint-enable complexity */

Fund.propTypes = {
  fund: PropTypes.shape({
    id: PropTypes.number,
    nameAndStock: PropTypes.string.isRequired,
    published: PropTypes.bool,
    active: PropTypes.bool.isRequired,
    publicUrl: PropTypes.string,
    publishable: PropTypes.bool.isRequired,
    company: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      partnership: PropTypes.shape({
        type: PropTypes.string.isRequired,
      }),
    }),
    dataReviews: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      current: PropTypes.string,
      toReview: PropTypes.string,
      createdAt: PropTypes.string,
    })).isRequired,
  }).isRequired,
};
